import React, { useState } from 'react';
import { Loader2, Plus, Trash2, Copy, RotateCcw, Lock } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

// ── VersionDialog (iter641 — Reset Draft + Scenario branching) ────
// • Lock the current draft as an immutable LOCKED version (original behaviour)
// • Save the current draft as an editable SCENARIO branch (new)
// • Restore a SCENARIO back into the active draft (new)
// • Write a LOCKED version to the schedule (original behaviour)
// • Delete any version (both LOCKED and SCENARIO)
export function VersionDialog({ versions, dateRange, onClose, onChanged, onDraftChanged }) {
  const { toast } = useToast();
  const [lockName, setLockName] = useState('');
  const [lockNotes, setLockNotes] = useState('');
  const [locking, setLocking] = useState(false);

  const [scenarioName, setScenarioName] = useState('');
  const [scenarioNotes, setScenarioNotes] = useState('');
  const [savingScenario, setSavingScenario] = useState(false);

  const [busyId, setBusyId] = useState(null);

  const lockedVersions  = versions.filter(v => v.status === 'LOCKED');
  const scenarioVersions = versions.filter(v => v.status === 'SCENARIO');

  // ── Lock as immutable version ─────────────────────────────────
  const lockNow = async () => {
    if (!lockName.trim()) {
      toast({ title: 'Version name required', variant: 'destructive' });
      return;
    }
    setLocking(true);
    try {
      const r = await api.post('/reports/revenue-budget/versions', {
        name: lockName.trim(),
        date_range_start: dateRange.start,
        date_range_end: dateRange.end,
        notes: lockNotes || null,
      });
      toast({
        title: 'Version locked',
        description: `Total £${(r.data?.totals?.grand_total || 0).toLocaleString('en-GB')}`,
      });
      setLockName(''); setLockNotes('');
      await onChanged();
    } catch (err) {
      toast({ title: 'Lock failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setLocking(false); }
  };

  // ── Save draft as Scenario ────────────────────────────────────
  const saveAsScenario = async () => {
    if (!scenarioName.trim()) {
      toast({ title: 'Scenario name required', variant: 'destructive' });
      return;
    }
    setSavingScenario(true);
    try {
      const r = await api.post('/reports/revenue-budget/draft/save-as-scenario', {
        name: scenarioName.trim(),
        date_range_start: dateRange.start,
        date_range_end: dateRange.end,
        notes: scenarioNotes || null,
      });
      toast({
        title: 'Scenario saved',
        description: `"${scenarioName.trim()}" — draft is unchanged, you can keep editing.`,
      });
      setScenarioName(''); setScenarioNotes('');
      await onChanged();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSavingScenario(false); }
  };

  // ── Restore Scenario → Draft ──────────────────────────────────
  const restoreScenario = async (v) => {
    if (!window.confirm(
      `Restore scenario "${v.name}" to the active draft?\n\n`
      + `This will REPLACE the current draft with the data saved in this scenario.\n`
      + `The scenario itself is preserved and can be restored again later.`
    )) return;
    setBusyId(v.id);
    try {
      const r = await api.post(`/reports/revenue-budget/versions/${v.id}/restore`);
      toast({
        title: 'Draft restored',
        description: `Draft now matches scenario "${v.name}" (${r.data?.rows_restored ?? 0} override rows).`,
      });
      if (onDraftChanged) await onDraftChanged();
      else await onChanged();
    } catch (err) {
      toast({ title: 'Restore failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  // ── Write LOCKED version to schedule ─────────────────────────
  const writeback = async (v) => {
    if (!window.confirm(
      `Write this version's projected audits into the schedule as SCHEDULED jobs?\n\n`
      + `Version: ${v.name}\n`
      + `Range: ${v.date_range_start} → ${v.date_range_end}\n\n`
      + `This creates one job per projected audit date. Cannot be re-run for the same version.`
    )) return;
    setBusyId(v.id);
    try {
      const r = await api.post(`/reports/revenue-budget/versions/${v.id}/writeback`);
      toast({ title: 'Written to schedule', description: `${r.data?.jobs_created || 0} jobs created.` });
      await onChanged();
    } catch (err) {
      toast({ title: 'Write-back failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const removeVersion = async (v) => {
    const label = v.status === 'SCENARIO' ? 'scenario' : 'version';
    if (!window.confirm(`Delete ${label} "${v.name}"?${v.status === 'LOCKED' ? ' Write-back jobs (if any) stay on the schedule.' : ''}`)) return;
    try {
      await api.delete(`/reports/revenue-budget/versions/${v.id}`);
      toast({ title: 'Deleted' });
      await onChanged();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="version-dialog" className="max-w-[95vw] w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Versions &amp; Scenarios</DialogTitle>
        </DialogHeader>

        {/* ── Save as Scenario (non-destructive) ─────────────────── */}
        <div className="border rounded-lg p-4 space-y-3 bg-emerald-50 border-emerald-200">
          <div className="flex items-center gap-2">
            <Copy className="h-4 w-4 text-emerald-700" />
            <span className="text-sm font-semibold text-emerald-800">Save current draft as a Scenario</span>
          </div>
          <p className="text-xs text-emerald-700">
            Creates a named copy of the draft at this point in time. The draft stays intact — you can keep editing. Restore it any time to bring this snapshot back.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Scenario name</Label>
              <Input value={scenarioName} onChange={(e) => setScenarioName(e.target.value)}
                placeholder="e.g. 2027 Optimistic" data-testid="scenario-name-input" />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={scenarioNotes} onChange={(e) => setScenarioNotes(e.target.value)}
                placeholder="e.g. Uplifts for new sites included"
                data-testid="scenario-notes-input" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveAsScenario} disabled={savingScenario}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="save-scenario-btn">
              {savingScenario ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4 mr-1" />}
              Save as Scenario
            </Button>
          </div>
        </div>

        {/* ── Lock as immutable version ───────────────────────────── */}
        <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-slate-600" />
            <span className="text-sm font-semibold text-slate-700">Lock current draft as a permanent version</span>
          </div>
          <p className="text-xs text-slate-500">
            Creates a read-only archived snapshot. Locked versions can be written to the schedule but cannot be restored to the draft.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Version name</Label>
              <Input value={lockName} onChange={(e) => setLockName(e.target.value)}
                placeholder="e.g. 2027 Q1 Approved"
                data-testid="version-name" />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={lockNotes} onChange={(e) => setLockNotes(e.target.value)}
                placeholder="e.g. Board-approved plan"
                data-testid="version-notes" />
            </div>
            <div className="md:col-span-2 text-xs text-slate-500">
              Snapshots the draft as-is for {dateRange.start} → {dateRange.end}. All overrides and uplifts bake in permanently.
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={lockNow} disabled={locking} variant="outline" data-testid="lock-version-btn">
              {locking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4 mr-1" />}
              Lock version
            </Button>
          </div>
        </div>

        {/* ── Saved Scenarios ─────────────────────────────────────── */}
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <Copy className="h-4 w-4 text-emerald-600" />
            Scenarios ({scenarioVersions.length})
            <span className="text-xs font-normal text-slate-500">— restore any of these to the active draft</span>
          </div>
          {scenarioVersions.length === 0 ? (
            <div className="text-xs text-slate-500 italic py-3 text-center border rounded bg-slate-50">
              No scenarios yet. Use "Save as Scenario" above to branch the current draft.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-emerald-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Range</th>
                    <th className="text-right px-3 py-2">Grand £</th>
                    <th className="text-left px-3 py-2">Saved by</th>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="px-3 py-2 w-36"></th>
                  </tr>
                </thead>
                <tbody>
                  {scenarioVersions.map(v => (
                    <tr key={v.id} className="border-t" data-testid={`scenario-row-${v.id}`}>
                      <td className="px-3 py-2 font-medium">
                        {v.name}
                        {v.notes && <div className="text-[10px] text-slate-500 mt-0.5">{v.notes}</div>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{v.date_range_start} → {v.date_range_end}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        £{(v.totals?.grand_total || 0).toLocaleString('en-GB')}
                      </td>
                      <td className="px-3 py-2">{v.locked_by_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                        {v.locked_at ? new Date(v.locked_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-right flex items-center justify-end gap-1">
                        <button type="button" onClick={() => restoreScenario(v)}
                          disabled={busyId === v.id}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-emerald-400 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 disabled:opacity-50"
                          data-testid={`restore-scenario-${v.id}`}>
                          {busyId === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                          Restore to draft
                        </button>
                        <button type="button" onClick={() => removeVersion(v)}
                          className="p-1 rounded hover:bg-rose-100 text-rose-600"
                          title="Delete scenario"
                          data-testid={`delete-scenario-${v.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Locked versions ─────────────────────────────────────── */}
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
            <Lock className="h-4 w-4 text-slate-500" />
            Locked versions ({lockedVersions.length})
            <span className="text-xs font-normal text-slate-500">— immutable archives, writeable to schedule</span>
          </div>
          {lockedVersions.length === 0 ? (
            <div className="text-xs text-slate-500 italic py-3 text-center border rounded bg-slate-50">
              No locked versions yet. Lock the current draft to create a permanent archive.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Range</th>
                    <th className="text-right px-3 py-2">Grand £</th>
                    <th className="text-left px-3 py-2">Locked by</th>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Write-back</th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {lockedVersions.map(v => (
                    <tr key={v.id} className="border-t" data-testid={`version-row-${v.id}`}>
                      <td className="px-3 py-2 font-medium">
                        {v.name}
                        {v.notes && <div className="text-[10px] text-slate-500 mt-0.5">{v.notes}</div>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{v.date_range_start} → {v.date_range_end}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        £{(v.totals?.grand_total || 0).toLocaleString('en-GB')}
                      </td>
                      <td className="px-3 py-2">{v.locked_by_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                        {v.locked_at ? new Date(v.locked_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-3 py-2">
                        {v.writeback_at ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px]">
                            ✓ {v.writeback_count} jobs
                          </span>
                        ) : (
                          <button type="button" onClick={() => writeback(v)}
                            disabled={busyId === v.id}
                            className="text-[11px] px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-100"
                            data-testid={`writeback-version-${v.id}`}>
                            {busyId === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Write to schedule'}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" onClick={() => removeVersion(v)}
                          className="p-1 rounded hover:bg-rose-100 text-rose-600"
                          title="Delete version"
                          data-testid={`delete-version-${v.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
